import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";

import CameraScan from "components/CameraScan/CameraScan";
import MDButton from "components/MDButton";
import CellPhoneTest from "assets/images/step3.svg?react";
import { useTranslation } from "react-i18next";
import { LoaderModelModal } from "components/LoaderModelModal/LoaderModelModal";
import { useAppDispatch, useAppSelector } from "store/hooks";
import configuredStore from "store/store";
import { getSessionId, getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { endCameraSession, startCameraSession } from "services/sentryTiming";
import { useStyles } from "./Step3.styles";
import { CameraDeniedModal } from "components/CameraDeniedModal/CameraDeniedModal";
import { checkCameraPermission } from "utils/camera.util";
import { sentryLogException, sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";

import { processImage, resetImageProcessingSession, getModelMetaDataTags } from "services/models";
import {
  setShouldStopImageProcess,
  setImages,
  setMessage,
  clearResult,
} from "store/slices/imageRecognition";

const ERROR_RETRY_DELAY_MS = 2000;

interface StepProps {
  handleUpdateStep: (step: number) => void;
}

const Step3: FC<StepProps> = ({ handleUpdateStep }) => {
  const dispatch = useAppDispatch();
  const [showCamera, setShowCamera] = React.useState(false);
  const shouldActiveFiltering = useRef(true);
  const cameraSessionStarted = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const [retryErrorMessage, setRetryErrorMessage] = useState<string | null>(null);
  const [permisionDeniedVissible, setPermisionDeniedVissible] = useState(false);
  const [screenOrientation, setScreenOrientation] =
    useState("portrait-primary");
  const msg = useAppSelector((state) => state.imageRecognition.msg);
  const status = useAppSelector((state) => state.imageRecognition.status);
  const result = useAppSelector((state) => state.imageRecognition.result);
  const isModelsLoaded = useAppSelector(
    (state) => state.imageRecognition.isModelsLoaded
  );

  const shouldStopImageProcess = useAppSelector(
    (state) => state.imageRecognition.shouldStopImageProcess
  );

  const theme = useTheme();
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const sessionId = useAppSelector(getSessionId);

  const startedTestLoggedRef = useRef(false);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const handleClearRetryState = useCallback(() => {
    clearRetryTimer();
    setRetryErrorMessage(null);
  }, [clearRetryTimer]);

  useEffect(() => {
    if (startedTestLoggedRef.current) {
      return;
    }
    startedTestLoggedRef.current = true;
    const state = configuredStore.getState();
    const sessionIdForLog = getSessionId(state);
    const testTypeForLog = getTestTypeValue(state);
    sentryLogMessage({
      message: "Started test",
      level: "info",
      tags: {
        component: "Step3",
        category: "user_journey",
        event_type: SENTRY_EVENT_TYPES.STARTED_TEST,
        testType: String(testTypeForLog),
        ...getModelMetaDataTags(),
      },
      extra: {
        sessionId: sessionIdForLog ?? undefined,
        timestamp: new Date().toISOString(),
      },
    });
  }, []);

  const resetScanSessionState = useCallback(() => {
    handleClearRetryState();
    resetImageProcessingSession();
    dispatch(setImages([]));
    dispatch(setMessage({ msg: "", status: "PROGRESS" }));

    const currentResult = configuredStore.getState().imageRecognition.result;
    if (currentResult === "MODEL_ERROR") {
      dispatch(clearResult());
    }
  }, [dispatch, handleClearRetryState]);

  const openCamera = useCallback(async () => {
    try {
      await checkCameraPermission();
      resetScanSessionState();
      dispatch(setShouldStopImageProcess(false));
      setPermisionDeniedVissible(false);
      
      // Start camera Sentry session timing
      startCameraSession(sessionId);
      cameraSessionStarted.current = true;
      
      setShowCamera(true);
    } catch {
      setPermisionDeniedVissible(true);
    }
  }, [dispatch, resetScanSessionState, sessionId]);

  const closeCamera = useCallback(() => {
    // Read the current result from Redux directly to avoid stale closure issues
    const currentResult = configuredStore.getState().imageRecognition.result;
    // Don't close camera if REJECTED - user needs to retry
    if (currentResult === "REJECTED") {
      return;
    }
    if (cameraSessionStarted.current) {
      endCameraSession(0);
      cameraSessionStarted.current = false;
    }
    // Stop capture before reset so clearing MODEL_ERROR cannot re-enable a frame
    dispatch(setShouldStopImageProcess(true));
    resetScanSessionState();
    setShowCamera(false);
  }, [dispatch, resetScanSessionState]);

  const setStyle = () => {
    const orientationType =
      document.body.scrollHeight < document.body.scrollWidth
        ? "landscape-primary"
        : "portrait-primary";

    setScreenOrientation(orientationType);
  };

  const captureImage = useCallback(async (data: any) => {
    const processImageData = {
      data: data.img,
      index: data.index,
      shouldActiveFiltering: shouldActiveFiltering,
    };
    try {
      if (!shouldStopImageProcess && !result) {
        await processImage(processImageData);
      } else {
        console.log("Image process stopped");
      }
    } catch (error) {
      console.error("processImage ERROR: ", error);
      sentryLogException({
        error,
        tags: {
          component: 'Step3',
          category: 'processImage',
          testType,
          ...getModelMetaDataTags(),
        },
        extra: {
          sessionId,
          imageIndex: processImageData.index,
          timestamp: new Date().toISOString(),
        },
      });
    }
    // worker.processImage(processImageData, imageStack);
  }, [shouldStopImageProcess, result]);

  useEffect(() => {
    if (!showCamera) {
      handleClearRetryState();
      return;
    }

    if (status !== "ERROR") {
      return;
    }

    // Avoid stacking timers if ERROR is already being handled
    if (retryTimerRef.current !== null) {
      return;
    }

    // Immediately purge session state and pause capture so the error stays visible
    resetImageProcessingSession();
    dispatch(setImages([]));
    dispatch(setShouldStopImageProcess(true));
    setRetryErrorMessage(msg);

    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      setRetryErrorMessage(null);

      dispatch(setMessage({ msg: "", status: "PROGRESS" }));

      const currentResult = configuredStore.getState().imageRecognition.result;
      if (currentResult === "MODEL_ERROR") {
        dispatch(clearResult());
      }

      resetImageProcessingSession();
      dispatch(setShouldStopImageProcess(false));
    }, ERROR_RETRY_DELAY_MS);
  }, [status, showCamera, msg, dispatch, handleClearRetryState]);

  useEffect(() => {
    setStyle();
    function handleResize() {
      setStyle();
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (result === "INVALID") {
      handleUpdateStep(6); // Go directly to ModalsActions
    } else if (result === "POSITIVE" || result === "NEGATIVE") {
      handleUpdateStep(5); // Go to TestResults (POSITIVE or NEGATIVE)
    }
    // Do NOT close camera or navigate on REJECTED - keep camera open for retry
  }, [result, handleUpdateStep]);

  useEffect(() => {
    if (!showCamera) {
      return;
    }

    const isDefinitiveResult =
      result === "POSITIVE" || result === "NEGATIVE" || result === "INVALID";

    if (isDefinitiveResult) {
      setShowCamera(false);
    }
  }, [showCamera, result]);

  useEffect(() => {
    return () => {
      clearRetryTimer();
      if (cameraSessionStarted.current) {
        endCameraSession(0);
        cameraSessionStarted.current = false;
      }
      resetImageProcessingSession();
    };
  }, [clearRetryTimer]);

  if (permisionDeniedVissible) {
    return (
      <CameraDeniedModal tryAgain={() => setPermisionDeniedVissible(false)} />
    );
  }

  return (
    <div className={cx(classes.container, showCamera && classes.containerCameraActive)}>
      {!showCamera && (
        <>
          <LoaderModelModal
            modelLoaded={isModelsLoaded}
            title={t(`${testTypeLocaleKey}:step3screen:modalTitle`)}
            description={t(`${testTypeLocaleKey}:step3screen:pleaseImproveConnection`)}
          />
          <div
            hidden={screenOrientation === "landscape-primary"}
            className={classes.bodyContainer}
          >
            <div className={classes.bodyTitle}>{t(`${testTypeLocaleKey}:step3screen:bodyTitle`)}</div>
            <div className={classes.bodyText}>{t(`${testTypeLocaleKey}:step3screen:bodyText`)}</div>
            <div className={classes.bodyImage}>
              <CellPhoneTest />
            </div>
          </div>
          <MDButton
            style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
            color={"primary"}
            variant="contained"
            type="submit"
            onClick={openCamera}
            disabled={!isModelsLoaded}
          >
            {t(`${testTypeLocaleKey}:step3screen:captureButton`)}
          </MDButton>
        </>
      )}
      {showCamera && (
        <CameraScan
          status={retryErrorMessage !== null ? "ERROR" : status}
          message={retryErrorMessage ?? msg}
          captureEnabled={!shouldStopImageProcess && !result}
          onImageCapture={captureImage}
          onClose={closeCamera}
          showNotSureOverlay={result === "REJECTED"}
          notSureMessage={`${testTypeLocaleKey}:step3screen:rejectMessage`}
        />
      )}
    </div>
  );
};

export default Step3;
